import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import type { SearchCohortEntriesInput } from '~/graphql/gen/graphql';
import FormMultiSelect from '~/shared/components/FormMultiSelect';
import FilterTextWithAdd from './FilterTextWithAdd';
import { theme } from '~/shared/styles/theme';
import {
  AGE_BAND_OPTIONS,
  CENSUS_REGION_OPTIONS,
  INHERITANCE_PATTERN_OPTIONS,
  LAB_SOURCE_OPTIONS,
  ONSET_TYPE_OPTIONS,
  RACE_ETHNICITY_OPTIONS,
  SEX_ASSIGNED_AT_BIRTH_OPTIONS,
  TEST_TYPE_OPTIONS,
} from '~/modules/vault/constants';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: currentYear - 2021 + 1 },
  (_, i) => 2021 + i,
).map((year) => ({
  value: String(year),
  label: String(year),
}));

export type FilterFormValues = Omit<
  SearchCohortEntriesInput,
  'yearsOfSample'
> & {
  yearsOfSample?: string[];
};

export function normalizeFilters(
  values: FilterFormValues,
): SearchCohortEntriesInput {
  const out: SearchCohortEntriesInput = {};
  if (values.ageBands?.length) out.ageBands = values.ageBands;
  if (values.censusRegions?.length) out.censusRegions = values.censusRegions;
  if (values.raceEthnicities?.length)
    out.raceEthnicities = values.raceEthnicities;
  if (values.sexes?.length) out.sexes = values.sexes;
  if (values.geneSymbol?.length) {
    const trimmed = values.geneSymbol
      .map((symbol) => symbol.trim())
      .filter(Boolean);
    if (trimmed.length) out.geneSymbol = trimmed;
  }
  if (values.testTypes?.length) out.testTypes = values.testTypes;
  if (values.labSources?.length) out.labSources = values.labSources;
  if (values.inheritancePatterns?.length)
    out.inheritancePatterns = values.inheritancePatterns;
  if (values.diagnoses?.length) {
    const trimmed = values.diagnoses
      .map((diagnosis) => diagnosis.trim())
      .filter(Boolean);
    if (trimmed.length) out.diagnoses = trimmed;
  }
  if (values.keySymptoms?.length) {
    const trimmed = values.keySymptoms
      .map((symptom) => symptom.trim())
      .filter(Boolean);
    if (trimmed.length) out.keySymptoms = trimmed;
  }
  if (values.keyLabs?.length) {
    const trimmed = values.keyLabs.map((lab) => lab.trim()).filter(Boolean);
    if (trimmed.length) out.keyLabs = trimmed;
  }
  if (values.onsetTypes?.length) out.onsetTypes = values.onsetTypes;
  if (values.treatmentExposure?.length) {
    const trimmed = values.treatmentExposure
      .map((exposure) => exposure.trim())
      .filter(Boolean);
    if (trimmed.length) out.treatmentExposure = trimmed;
  }
  if (values.nutritionalManagement?.length) {
    const trimmed = values.nutritionalManagement
      .map((management) => management.trim())
      .filter(Boolean);
    if (trimmed.length) out.nutritionalManagement = trimmed;
  }
  if (values.surgicalIntervention?.length) {
    const trimmed = values.surgicalIntervention
      .map((intervention) => intervention.trim())
      .filter(Boolean);
    if (trimmed.length) out.surgicalIntervention = trimmed;
  }
  if (values.yearsOfSample?.length) {
    const years = values.yearsOfSample
      .map(Number)
      .filter((num) => !Number.isNaN(num));
    if (years.length) out.yearsOfSample = years;
  }
  return out;
}

export interface Props {
  onChange: (filters: SearchCohortEntriesInput) => void;
  onClear: () => void;
}

const defaultFilterValues: FilterFormValues = {
  geneSymbol: [],
  diagnoses: [],
  keySymptoms: [],
  keyLabs: [],
  treatmentExposure: [],
  nutritionalManagement: [],
  surgicalIntervention: [],
};

const FilterPanel = ({ onChange, onClear }: Props) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const { control, watch, getValues, reset } = useForm<FilterFormValues>({
    defaultValues: defaultFilterValues,
  });

  useEffect(() => {
    onChangeRef.current(normalizeFilters(getValues()));
    const subscription = watch((data) => {
      onChangeRef.current(normalizeFilters(data as FilterFormValues));
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues]);

  return (
    <Panel
      as="form"
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <HeaderRow
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography
          variant="body1"
          color={theme.palette.custom.darkBrown}
          fontWeight={700}
        >
          Filters
        </Typography>
        <ClearButton
          type="button"
          variant="text"
          onClick={() => {
            reset(defaultFilterValues);
            onClear();
          }}
        >
          Clear All
        </ClearButton>
      </HeaderRow>
      <Divider sx={{ borderColor: theme.palette.custom.cohortCardBorder }} />
      <PanelContent>
        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ChevronIcon />}
            sx={accordionSummarySx}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color={theme.palette.custom.darkBrown}
            >
              Demographics
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <FormMultiSelect
                name="ageBands"
                control={control}
                label="AGE BAND"
                options={AGE_BAND_OPTIONS}
              />
              <FormMultiSelect
                name="sexes"
                control={control}
                label="SEX ASSIGNED AT BIRTH"
                options={SEX_ASSIGNED_AT_BIRTH_OPTIONS}
              />
              <FormMultiSelect
                name="censusRegions"
                control={control}
                label="CENSUS REGION"
                options={CENSUS_REGION_OPTIONS}
              />
              <FormMultiSelect
                name="raceEthnicities"
                control={control}
                label="RACE / ETHNICITY"
                options={RACE_ETHNICITY_OPTIONS}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ChevronIcon />}
            sx={accordionSummarySx}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color={theme.palette.custom.darkBrown}
            >
              Genetic Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <FilterTextWithAdd
                name="geneSymbol"
                control={control}
                label="GENE SYMBOL"
                placeholder="Filter by symbol"
              />
              <FormMultiSelect
                name="testTypes"
                control={control}
                label="TEST TYPE"
                options={TEST_TYPE_OPTIONS}
              />
              <FormMultiSelect
                name="labSources"
                control={control}
                label="LAB SOURCE"
                options={LAB_SOURCE_OPTIONS}
              />
              <FormMultiSelect
                name="inheritancePatterns"
                control={control}
                label="INHERITANCE PATTERN"
                options={INHERITANCE_PATTERN_OPTIONS}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ChevronIcon />}
            sx={accordionSummarySx}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color={theme.palette.custom.darkBrown}
            >
              Clinical Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <FilterTextWithAdd
                name="diagnoses"
                control={control}
                label="DIAGNOSES"
                placeholder="Search by diagnoses"
              />
              <FilterTextWithAdd
                name="keySymptoms"
                control={control}
                label="KEY SYMPTOMS"
                placeholder="Search by symptoms"
              />
              <FilterTextWithAdd
                name="keyLabs"
                control={control}
                label="KEY LABS"
                placeholder="Search by labs"
              />
              <FormMultiSelect
                name="onsetTypes"
                control={control}
                label="ONSET TYPE"
                options={ONSET_TYPE_OPTIONS}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ChevronIcon />}
            sx={accordionSummarySx}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color={theme.palette.custom.darkBrown}
            >
              Treatment & Care
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <FilterTextWithAdd
                name="treatmentExposure"
                control={control}
                label="TREATMENT EXPOSURE"
                placeholder="Search by treatment"
              />
              <FilterTextWithAdd
                name="nutritionalManagement"
                control={control}
                label="NUTRITIONAL MANAGEMENT"
                placeholder="Search by name"
              />
              <FilterTextWithAdd
                name="surgicalIntervention"
                control={control}
                label="SURGICAL INTERVENTION"
                placeholder="Search by interventions"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary
            expandIcon={<ChevronIcon />}
            sx={accordionSummarySx}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color={theme.palette.custom.darkBrown}
            >
              Temporal
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <FormMultiSelect
                name="yearsOfSample"
                control={control}
                label="YEAR OF SAMPLE"
                options={YEAR_OPTIONS}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </PanelContent>
    </Panel>
  );
};

export default FilterPanel;

const Panel = styled(Box)({
  width: theme.tokens.layout.filterPanelWidth,
  minHeight: '100%',
  flexShrink: 0,
  backgroundColor: theme.palette.common.white,
  borderRight: `1px solid ${theme.palette.custom.cohortCardBorder}`,
});

const HeaderRow = styled(Stack)({
  padding: theme.spacing(3, 2.5, 3, 5),
});

const ClearButton = styled(Button)(() => ({
  fontSize: 14,
  fontWeight: 700,
  color: theme.palette.custom.darkBrown,
  minWidth: 0,
}));

const PanelContent = styled(Box)({
  padding: theme.spacing(4, 2.5, 2, 2.5),
});

const ChevronIcon = styled(Box)({
  width: 0,
  height: 0,
  borderLeft: '6.65px solid transparent',
  borderRight: '6.65px solid transparent',
  borderTop: `7.7px solid ${theme.palette.custom.darkBrown}`,
});

const accordionSx = {
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 },
};

const accordionSummarySx = {
  minHeight: 40,
  '& .MuiAccordionSummary-content': { my: 1 },
};
